import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmt, pct } from "@/lib/format";
import { KLayout, KBadge, KDemo } from "@/pages/kreonis/KLayout";
import { Seo } from "@/components/Seo";
import { PriceChart } from "@/components/PriceChart";
import { ArrowRight, ShieldCheck, Globe, BarChart3 } from "lucide-react";

export default function KHome() {
  const [assets, setAssets] = useState([]);
  const [candles, setCandles] = useState([]);
  useEffect(() => {
    api.get("/markets").then((r) => setAssets(r.data.assets.slice(0, 4))).catch(() => {});
    api.get("/markets/BTC%2FUSD/candles?timeframe=4h&bars=120").then((r) => setCandles(r.data.candles)).catch(() => {});
  }, []);
  return (
    <KLayout>
      <Seo title="KREONIS — Institutional AI Market Intelligence" description="Understand Markets. Find Signals. Manage Risk." />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(16,185,129,0.12), transparent 70%)" }} />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <div>
            <KBadge>⚡ Institutional AI Market Intelligence <KDemo /></KBadge>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">Understand Markets.<br />Find Signals.<br /><span className="text-emerald-400">Manage Risk.</span></h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">KREONIS synthesizes global market data and artificial intelligence to help institutional allocators understand macro conditions, isolate high-fidelity predictive signals, and implement advanced risk-management frameworks.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="khero-request-access" className="group inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400">Request Access <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
              <Link to="/kreonis/intelligence" data-testid="khero-explore" className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-6 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800">Explore Intel Feed</Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-400" /> Non-custodial infrastructure</span>
              <span className="flex items-center gap-1.5"><Globe className="h-4 w-4 text-emerald-400" /> Global market coverage</span>
              <span className="flex items-center gap-1.5"><BarChart3 className="h-4 w-4 text-emerald-400" /> Institutional-grade analytics</span>
            </div>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div><div className="font-display text-lg font-bold text-slate-100">BTC/USD</div><div className="text-xs text-slate-500">4H · macro regime preview</div></div><KDemo />
            </div>
            <div className="mt-2"><PriceChart candles={candles} height={200} color="#10b981" /></div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {assets.map((a) => (
                <div key={a.symbol} className="rounded-lg border border-slate-800 p-3">
                  <div className="text-xs text-slate-400">{a.symbol}</div>
                  <div className="font-mono text-sm font-semibold text-slate-100">{fmt(a.price)}</div>
                  <div className={`text-xs ${a.change_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{pct(a.change_pct)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </KLayout>
  );
}
